import { useMemo } from 'react';
import {
  useFetchInfiniteES,
  useApiClient,
} from '@marageti/z4-lib';
import {
  ReferralType,
  TripRequest,
  ChecklistItem,
} from '@marageti/z4-sdk/lib/travel';
import {
  FilterConstraint, FilterOp, SearchRequest, SearchResult,
} from '@marageti/z4-sdk/lib/entity';

/** Inclusive calendar-day range via Elasticsearch date math (`yyyy-MM-dd||/d`). */
function appendEsInclusiveDayRange(
  filterBy: FilterConstraint[],
  field: string,
  from: string,
  to: string,
) {
  const d0 = from?.trim();
  const d1 = to?.trim();
  if (d0) {
    filterBy.push({ field, op: FilterOp.FilterOpGTE, value: [`${d0}||/d`] });
  }
  if (d1) {
    filterBy.push({ field, op: FilterOp.FilterOpLTE, value: [`${d1}||/d`] });
  }
}

const tagValues: any = {
  vip: ['VIP'],
  repeatTraveler: ['true'],
  prl: [ReferralType.LifetimePartnerReferral, ReferralType.OneTimePartnerReferral],
  badApple: ['BAD_APPLE'],
  redFlag: ['RED_FLAG'],
  travelAgent: ['TRAVEL_AGENT'],
  junk: ['JUNK'],
  foby: ['FOBY'],
};

const tagFields: any = {
  vip: 'travelers.tags',
  repeatTraveler: 'repeatTraveler',
  prl: 'referralType',
  badApple: 'travelers.tags',
  redFlag: 'travelers.tags',
  travelAgent: 'travelers.tags',
  junk: 'travelers.tags',
  foby: 'travelers.tags',
};

// Update this interface when new search fields are added
// Use plural for arrays and singular for single values so we can support destination and destinations, for example.
interface PossibleSearchFields {
  assignee?: string;
  checklist?: ChecklistItem[];
  destinations?: string[];
  /** Inclusive departure date filter (`yyyy-MM-dd`). */
  departureDateFrom?: string;
  departureDateTo?: string;
  excludeUnassigned?: boolean;
  fullText?: string;
  /** Inclusive request-created filter on `createdAt` (`yyyy-MM-dd`). */
  matchDateFrom?: string;
  matchDateTo?: string;
  order?: string;
  sort?: string;
  status?: string[];
  tag?: string;
  traveler?: string;
}

const useTripRequestSearch = ({
  assignee = '',
  checklist = [],
  destinations = [],
  departureDateFrom = '',
  departureDateTo = '',
  excludeUnassigned = false,
  fullText = '',
  matchDateFrom = '',
  matchDateTo = '',
  order = '',
  sort = '',
  status = [],
  tag = '',
  traveler = '',
}: PossibleSearchFields) => {
  const apiClient = useApiClient();

  // On rerender, create the search request
  const getSearchRequest = (): SearchRequest => {
    const filterBy: FilterConstraint[] = [];

    // handle checklist filter (for In Progress trips)
    if (checklist.length > 0) {
      filterBy.push({
        field: 'checklist',
        value: checklist,
        op: FilterOp.FilterOpTerm,
      });
    }

    // create status filter
    if (status.length > 0) {
      filterBy.push({ field: 'status', value: status });
    }
    // handle tag filter
    if (tag && tag !== 'ALL') {
      filterBy.push({ field: tagFields[tag], value: tagValues[tag] });
    }

    // handle assignee filter
    if (assignee && assignee !== 'ALL' && assignee !== 'UNASSIGNED') {
      filterBy.push({ field: 'assignedTo', value: [assignee] });
    }

    // handle unassigned filter
    if (assignee === 'UNASSIGNED') {
      filterBy.push({ field: 'assignedTo', value: [''] });
    }

    // exclude unassigned from results when flag is true and no specific assignee is selected
    if (excludeUnassigned && !assignee) {
      filterBy.push({ field: 'assignedTo', value: [''], op: FilterOp.FilterOpNot });
    }

    // handle destination filter
    if (destinations && destinations.length > 0) {
      filterBy.push({ field: 'destinations.id', value: destinations });
    }

    if (traveler) {
      filterBy.push({ field: 'travelers.id', value: [traveler] });
    }

    appendEsInclusiveDayRange(filterBy, 'createdAt', matchDateFrom, matchDateTo);
    appendEsInclusiveDayRange(filterBy, 'departureDate', departureDateFrom, departureDateTo);

    return {
      filterBy,
      fullText: fullText.length > 2 ? fullText : undefined,
      phrase: fullText.includes('@'),
      pagination: { limit: 30, page: 1 },
      ...(sort && order) && { sortBy: [{ field: sort, order }] },
    };
  };

  // Fetch and exicute search request
  const searchRequest = getSearchRequest();

  const {
    data,
    error,
    hasMore,
    isLoading,
    loadMore,
  } = useFetchInfiniteES<TripRequest>(apiClient.tripRequestsClient.postTripRequestSearches, searchRequest, { revalidateOnFocus: true, revalidateFirstPage: true });

  const tripRequests = useMemo(() => (
    data?.flatMap((searchResult: SearchResult<TripRequest>) => searchResult.hits)
  ), [data]);

  return {
    tripRequests,
    error,
    hasMore,
    isLoading,
    loadMore,
    totalHits: data?.[0]?.totalHits || 0,
  };
};

export default useTripRequestSearch;
